import {
  FinnairBoundItem,
  FinnairEligibilities,
  FinnairItineraryItem,
  FinnairItineraryItemFlight,
  FinnairItineraryItemFlightStatus,
  FinnairItineraryItemLayover,
  FinnairItineraryItemType,
  FinnairItineraryNote,
  FinnairLocation,
  FinnairTravelEndpoint,
} from '@fcom/dapi/api/models';
import { LocalDate, TzDate } from '@fcom/core/utils';
import { isComplexBooking } from '@fcom/manage-booking-common/utils';
import { FlightNotification } from '@fcom/common-booking';
import { isFlight } from '@fcom/common-booking/utils/flight.utils';
import { NotificationTheme } from '@fcom/ui-components';

import { BoundReaccommodationStatus, BoundReaccommodationType } from '../interfaces';

/**
 * Get notification message if user has selected flight that has different airport or date than original flight
 * @param cancelled bound that has been cancelled
 * @param selected new bound the user has selected
 */
export const getNotificationMessageForNewFlight = (
  cancelled: FinnairBoundItem,
  selected: FinnairBoundItem
): string | null => {
  const differentAirport = differentAirportSelected(cancelled, selected);
  const differentDate = differentDateSelected(cancelled, selected);

  if (differentAirport && differentDate) {
    return 'reaccommodation.notifications.differentAirportAndDate';
  } else if (differentDate) {
    return 'reaccommodation.notifications.differentDate';
  } else if (differentAirport) {
    return 'reaccommodation.notifications.differentAirport';
  }

  return null;
};

/**
 * Determine which part of the original journey the selected offer replaces.
 *
 * For a missed connection the passenger has already flown the first leg(s), so only alternatives for
 * the remaining journey are offered (e.g. HEL-ARN for an original OUL-ARN bound). Comparing such an
 * offer against the whole bound reports a false airport change, so the origin has to come from the
 * missed-connection leg instead. The destination is always the bound's final arrival. Falls back to
 * the whole bound when there is no missed connection, or when the offer replaces the whole journey
 * (its origin matches the bound's origin) — so a genuinely different origin is still reported.
 * @param cancelled bound that has been cancelled
 * @param selected new bound the user has selected
 */
const resolveComparisonScope = (
  cancelled: FinnairBoundItem,
  selected: FinnairBoundItem
): { departure?: FinnairTravelEndpoint; arrival?: FinnairTravelEndpoint } => {
  const original = cancelled?.disruptedBound?.disruptedBound ?? cancelled;
  const wholeBound = { departure: original?.departure, arrival: original?.arrival };

  const missedLeg = getMissedConnectionFlight(original?.itinerary);
  if (!missedLeg) {
    return wholeBound;
  }

  const remainingJourney = { departure: missedLeg.departure, arrival: original?.arrival };
  return remainingJourney.departure?.locationCode === selected?.departure?.locationCode ? remainingJourney : wholeBound;
};

/**
 * Determine if user has selected flight that has different location than in original flight
 * @param cancelled bound that has been cancelled
 * @param selected new bound the user has selected
 */
export const differentAirportSelected = (cancelled: FinnairBoundItem, selected: FinnairBoundItem): boolean => {
  const { departure, arrival } = resolveComparisonScope(cancelled, selected);
  return (
    departure?.locationCode !== selected.departure.locationCode ||
    arrival?.locationCode !== selected.arrival.locationCode
  );
};

/**
 * Determine if user has selected flight that has different date than in original flight
 * @param cancelled bound that has been cancelled
 * @param selected new bound the user has selected
 */
export const differentDateSelected = (cancelled: FinnairBoundItem, selected: FinnairBoundItem): boolean => {
  const { departure, arrival } = resolveComparisonScope(cancelled, selected);
  return (
    isDifferentDay(departure?.dateTime, selected.departure.dateTime) ||
    isDifferentDay(arrival?.dateTime, selected.arrival.dateTime)
  );
};

/**
 * Determine if the bound has a layover
 * @param bound a bound of an itinerary
 */
export const boundHasOvernightLayover = (bound: FinnairBoundItem): boolean => {
  return bound.itinerary.some((item: FinnairItineraryItem) => {
    return (
      item.type === FinnairItineraryItemType.LAYOVER &&
      (item as FinnairItineraryItemLayover).note === FinnairItineraryNote.HAS_NEXT_DAY_TRANSFER
    );
  });
};

/**
 * Determine if datetimes have different days
 * @param start datetime (e.g. 2019-09-13T14:26:19.828Z)
 * @param end datetime (e.g. 2019-09-13T14:26:19.828Z)
 */
export const isDifferentDay = (start: string, end: string): boolean => {
  return start?.split('T')[0] !== end?.split('T')[0];
};

/**
 * Get the bound type for a bound (outbound, inbound, complex)
 * @param bounds all bounds in the booking
 * @param index index for the bound to be determined
 */
export const getBoundType = (bounds: FinnairBoundItem[], index: number): BoundReaccommodationType => {
  if (isComplexBooking(bounds)) {
    return BoundReaccommodationType.COMPLEX;
  } else {
    return index === 0 ? BoundReaccommodationType.OUTBOUND : BoundReaccommodationType.INBOUND;
  }
};

export const getBoundTypeByBoundId = (bounds: FinnairBoundItem[], boundId: string): BoundReaccommodationType => {
  if (isComplexBooking(bounds)) {
    return BoundReaccommodationType.COMPLEX;
  } else {
    return bounds.findIndex((b) => b.id === boundId) === 0
      ? BoundReaccommodationType.OUTBOUND
      : BoundReaccommodationType.INBOUND;
  }
};

/**
 * Determine whether the given date falls within the bound's disruption-rebooking eligible date ranges.
 * A bound without any eligible date ranges is treated as not eligible (returns false). When no date is
 * provided (e.g. nothing selected yet) there is nothing to validate, so it is treated as eligible.
 * @param eligibilities order eligibilities containing the disruptionRebooking entries
 * @param boundId id of the bound whose eligible date ranges should be checked
 * @param date date to validate against the bound's eligible date ranges
 */
export const isDateWithinEligibleRange = (
  eligibilities: FinnairEligibilities,
  boundId: string,
  date: LocalDate | undefined
): boolean => {
  const ranges = eligibilities.disruptionRebooking.find(
    (eligibility) => eligibility.id === boundId
  )?.eligibleDateRanges;
  if (!ranges?.length) {
    return false;
  }
  if (!date || typeof (date as LocalDate).isBetween !== 'function') {
    return true;
  }
  return ranges.some((range) =>
    date.isBetween(TzDate.of(range.fromDateTime).toLocalDate(), TzDate.of(range.toDateTime).toLocalDate())
  );
};

/**
 * Get heading for a single bound based on type and status
 * @param type wheter bound is inbound or outbound
 * @param status wheter bound is rebooked, new or cancelled
 */
export const getBoundHeadingLabel = (type: BoundReaccommodationType, status: BoundReaccommodationStatus): string => {
  return `reaccommodation.boundHeadings.${type}.${status || BoundReaccommodationStatus.DEFAULT}`;
};

export const getFareFamilyLabel = (bound: FinnairBoundItem): string | undefined =>
  bound.fareFamily?.name || (bound?.itinerary[0] as FinnairItineraryItemFlight)?.cabinClass;

export const isFlightFlown = (flight: FinnairItineraryItemFlight | undefined): boolean => {
  return flight?.status === FinnairItineraryItemFlightStatus.FLOWN;
};

export const isBoundArrived = (bound: FinnairBoundItem): boolean => {
  return new Date(bound.arrival.dateTime).getTime() < Date.now();
};

export const getFirstNotFlownFlight = (
  flights: FinnairItineraryItemFlight[] | undefined
): FinnairItineraryItemFlight | undefined => {
  return flights?.find((flight) => !isFlightFlown(flight));
};

const isMissedConnectionFlight = (flight: FinnairItineraryItemFlight): boolean =>
  !!flight.flightStatusFlags?.missedConnection;

const isMissedConnectionDueToCancellationFlight = (flight: FinnairItineraryItemFlight): boolean =>
  flight.flightStatusFlags?.missedConnection?.reason === 'CANCELLATION';

export const getMissedConnectionFlight = (
  itinerary: FinnairItineraryItem[] | undefined
): FinnairItineraryItemFlight | undefined => {
  const flights = itinerary?.filter((item): item is FinnairItineraryItemFlight => isFlight(item));
  return flights?.find((flight) => isMissedConnectionFlight(flight) && !isFlightFlown(flight));
};

export const getFlightById = (
  itinerary: FinnairItineraryItem[] | undefined,
  id: string | undefined
): FinnairItineraryItemFlight | undefined => {
  if (!id || !itinerary) {
    return undefined;
  }

  return itinerary
    .filter((item): item is FinnairItineraryItemFlight => isFlight(item))
    .find((flight) => flight.id === id);
};

export const getFlightDetail = (
  flight: FinnairItineraryItemFlight | undefined,
  locations?: Record<string, FinnairLocation>
): (FinnairItineraryItemFlight & { locationTitle?: string }) | undefined => {
  if (!flight) {
    return undefined;
  }
  return {
    ...flight,
    locationTitle: locations ? getFlightLocationTitle(flight, locations) : undefined,
  };
};

export const getFlightLocationTitle = (
  flight: FinnairItineraryItemFlight,
  locations: Record<string, FinnairLocation>
): string => {
  const departureCity = locations[flight.departure.locationCode]?.cityName || '';
  const arrivalCity = locations[flight.arrival.locationCode]?.cityName || '';
  return `${departureCity} - ${arrivalCity}`;
};

/**
 * Get route title for a bound (e.g., "Helsinki - Amsterdam")
 * Uses first flight's departure city and last flight's arrival city.
 */
export const getBoundRouteTitle = (
  bound: FinnairBoundItem,
  locations?: Record<string, FinnairLocation>
): string | undefined => {
  if (!locations) {
    return undefined;
  }
  const flights = bound.itinerary.filter((item): item is FinnairItineraryItemFlight => isFlight(item));
  const firstFlight = flights[0];
  const lastFlight = flights[flights.length - 1];
  if (!firstFlight || !lastFlight) {
    return undefined;
  }
  const departureCity = locations[firstFlight.departure.locationCode]?.cityName;
  const arrivalCity = locations[lastFlight.arrival.locationCode]?.cityName;
  return departureCity && arrivalCity ? `${departureCity} - ${arrivalCity}` : undefined;
};

export const getFlightNotifications = (
  eligibilities: FinnairEligibilities,
  bound: FinnairBoundItem,
  includeNoChanges = true,
  offerBound?: FinnairBoundItem
): FlightNotification[] => {
  const eligibility = eligibilities.disruptionRebooking.find((b) => b.id === bound.id);
  const reason = bound.itinerary.filter(isFlight).some((i) => i.status === FinnairItineraryItemFlightStatus.FLOWN)
    ? 'ALREADY_FLOWN'
    : undefined;
  if (eligibility && !eligibility.isAllowedToUse && reason) {
    return [{ theme: NotificationTheme.INFO, key: `MMB.reaccommodation.notes.${reason}` }];
  }

  if (offerBound) {
    const key = getNotificationMessageForNewFlight(bound, offerBound);
    return key ? [{ theme: NotificationTheme.INFO, key }] : [];
  }

  return bound.disruptedBound?.disruptedBound
    ? (bound.disruptedBound?.disruptedBound.notes ?? []).map((note) => ({
        theme: NotificationTheme.DISRUPTION,
        key: `MMB.reaccommodation.notes.${note}`,
      }))
    : includeNoChanges
      ? [{ theme: NotificationTheme.INFO, key: 'MMB.reaccommodation.notes.NO_CHANGES' }]
      : [];
};

const disruptedFlight = (
  bound: FinnairBoundItem,
  predicate: (flight: FinnairItineraryItemFlight) => boolean
): boolean => bound.disruptedBound?.disruptedBound?.itinerary?.filter(isFlight).some(predicate) ?? false;

export const hasMissedConnection = (bound: FinnairBoundItem): boolean =>
  disruptedFlight(bound, isMissedConnectionFlight);

export const hasMissedConnectionDueToCancellation = (bound: FinnairBoundItem): boolean =>
  disruptedFlight(bound, isMissedConnectionDueToCancellationFlight);

export default {
  getFlightDetail,
  getFareFamilyLabel,
  getBoundHeadingLabel,
  getBoundType,
  isDifferentDay,
  boundHasOvernightLayover,
  differentDateSelected,
  differentAirportSelected,
  getNotificationMessageForNewFlight,
  hasMissedConnection,
  hasMissedConnectionDueToCancellation,
  isFlightFlown,
  getFirstNotFlownFlight,
  getMissedConnectionFlight,
  getFlightById,
  isBoundArrived,
};
